// Mega Nav (BEM) – reine Funktionalität, keine Hardcodes
// Erwartete Struktur siehe index.html (BEM-Klassen), IDs bleiben für ARIA-Relationen.
const DEV_ALWAYS_OPEN = false; // TODO: beim normalen Betrieb auf false setzen
// Desktop-Öffnungsmodus für Ebene 1: 'hover' oder 'click'
const DESKTOP_TRIGGER_MODE = 'click'; // bei Bedarf auf 'click' ändern
const SPRITE_PATH = '/wp-content/themes/carthago/assets/sprite.svg';

const createSpriteIcon = (name, className, width, height, viewBox) => {
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  if (className) svg.setAttribute('class', className);
  if (width) svg.setAttribute('width', width);
  if (height) svg.setAttribute('height', height);
  if (viewBox) svg.setAttribute('viewBox', viewBox);
  svg.setAttribute('aria-hidden', 'true');
  svg.setAttribute('focusable', 'false');

  const use = document.createElementNS('http://www.w3.org/2000/svg', 'use');
  const href = `${SPRITE_PATH}#${name}`;
  use.setAttribute('href', href);
  use.setAttributeNS('http://www.w3.org/1999/xlink', 'xlink:href', href);
  svg.appendChild(use);
  return svg;
};

class MenuSystem {
  constructor() {
    // State
    this.activeBtn = null;
    this.isMobileMenuOpen = false;
    this.prevFocused = null;

    // Cache DOM
    this.mainMenu        = document.getElementById('mainMenu');
    this.submenuPanel    = document.getElementById('submenuPanel');
    this.navRoot         = document.getElementById('unifiedNav');
    this.submenuSections = Array.from(document.querySelectorAll('.mega-nav__section'));

    this.mobileMenuButton  = document.getElementById('mobileMenuButton');
    this.mobileMenuOverlay = document.getElementById('mobileMenuOverlay');
    this.closeButton       = document.getElementById('closeButton');
    this.mobileMainMenu    = document.getElementById('mobileMainMenu');
    this.mobileSubmenu     = document.getElementById('mobileSubmenu');

    // Portal bookkeeping for mobile: remember original parents and placeholders
    this._originalParents  = new Map(); // key -> parentNode
    this._placeholders     = new Map(); // key -> Comment placeholder
    this._currentMobileKey = null;      // which submenu is currently portaled

    // Track moved "models aside" for Wohnmobile in mobile (teleport, not clone)
    this._wmAsidePlaceholders = new Map(); // asideElement -> { placeholder, parent }

    // Track moved shared linklist (.wm-links)
    this._wmLinksPlaceholder = null; // Comment node

    // Track which elements currently carry the mobile list padding utilities
    this._mobileListPadding = ['px-8', 'md:px-15', 'xl:px-0'];
    this._mobilePaddedElements = new Set();

    // Breakpoint tracking
    this._isDesktop = this.isDesktop();

    // Internal guards
    this._wmListClickBound = false;
    this._submenuCloseTimer = null;
    this._submenuClosingHandler = null;
    this._pendingReturnFocus = null;

    this.init();
  }

  getWohnmobileSection() {
    return this.submenuSections.find((section) => section.getAttribute('data-submenu-type') === 'wohnmobile') || null;
  }

  getWohnmobileMenuKey() {
    return this.getWohnmobileSection()?.getAttribute('data-submenu') || 'wohnmobile';
  }

  init() {
    // --- DEV: immer Wohnmobile offen halten (Desktop-Panel) ---
    if (DEV_ALWAYS_OPEN) {
      const wohnmobileMenuKey = this.getWohnmobileMenuKey();
      const wohnmobilePanel = document.getElementById(`submenu-${wohnmobileMenuKey}`);
      const wohnmobileBtn   = document.getElementById(`btn-${wohnmobileMenuKey}`);
      if (wohnmobilePanel) wohnmobilePanel.hidden = false;
      if (wohnmobileBtn) wohnmobileBtn.setAttribute('aria-expanded', 'true');
      if (this.submenuPanel) this.submenuPanel.classList.add('active');
      // Desktop-Default für Wohnmobile
      this.wmSection = this.getWohnmobileSection();
      if (this.wmSection) this.activateBaureihe('vollintegriert', { autoModel: this.isDesktop() });
      // Ensure the desktop close buttons exist even in DEV always-open mode
      this._ensureDesktopCloseButtons();
      return;
    }

    this.setupDesktopMenu();
    this.setupMobileMenu();
    this.setupGlobalKeys();
    this.setupResponsiveBehavior();
    this.initWohnmobile();
    this._ensureDesktopCloseButtons();
  }

  // =========================
  // Desktop: Close buttons in submenu sections
  // =========================
  _ensureDesktopCloseButtons() {
    if (!this.isDesktop()) return;
    this.submenuSections.forEach((sec) => {
      if (sec.querySelector('.mega-nav__desktop-close')) return; // already present
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'mega-nav__desktop-close';
      btn.setAttribute('aria-label', 'Schließen');
      btn.innerHTML = `
        <svg class="mega-nav__icon--close" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1" aria-hidden="true" focusable="false">
          <line x1="19" y1="5" x2="5" y2="19"></line>
          <line x1="5" y1="5" x2="19" y2="19"></line>
        </svg>`;
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        this.closeAllSubmenus();
      });
      // Insert as last child so it can be positioned via CSS (top-right)
      sec.appendChild(btn);
    });
  }

  _removeDesktopCloseButtons() {
    this.submenuSections.forEach((sec) => {
      const btn = sec.querySelector('.mega-nav__desktop-close');
      if (btn) btn.remove();
    });
  }

  // =========================
  // Desktop
  // =========================
  setupDesktopMenu() {
    if (!this.mainMenu) return;
    const triggers = Array.from(this.mainMenu.querySelectorAll('.mega-nav__menu-button'));

    triggers.forEach((btn) => {
      // Guard to prevent click from re-opening immediately after keydown (Enter/Space)
      // and accidentally skipping the focus move into the panel
      let lastKbdOpenTs = 0;

      btn.addEventListener('mouseenter', () => {
        if (this.isDesktop() && this._desktopModeIs('hover')) this.openSubmenu(btn, { focusPanel: false });
      });
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const now = Date.now();
        if (!this.isDesktop()) return;
        // If a keyboard-driven open just happened, skip this synthetic click
        if (now - lastKbdOpenTs < 250) return;
        if (this._desktopModeIs('click') || this._desktopModeIs('hover')) {
          this.openSubmenu(btn, { focusPanel: false });
        }
      });
      // Keyboard open behaviour
      // Enter/Space: öffnen OHNE Fokusverschiebung
      // ArrowDown/ArrowUp: öffnen UND Fokus ins Panel (erster/letzter Eintrag – Erster per _focusFirstIn)
      btn.addEventListener('keydown', (e) => {
        if (!this.isDesktop()) return;
        const key = e.key;
        if (key === 'Enter' || key === ' ') {
          e.preventDefault();
          e.stopPropagation();
          lastKbdOpenTs = Date.now();
          this.openSubmenu(btn, { focusPanel: false });
        } else if (key === 'ArrowDown' || key === 'ArrowUp') {
          e.preventDefault();
          e.stopPropagation();
          lastKbdOpenTs = Date.now();
          this.openSubmenu(btn, { focusPanel: true });
        }
      });
    });

    // Close on mouseleave only in HOVER mode (click mode uses outside-click or the X button)
    if (this.navRoot) {
      this.navRoot.addEventListener('mouseleave', () => {
        if (this.isDesktop() && this._desktopModeIs('hover')) this.closeAllSubmenus();
      });
    }

    // Click outside the nav closes any open submenu (desktop only)
    document.addEventListener('click', (e) => {
      if (!this.isDesktop()) return;
      if (!this.submenuPanel || !this.submenuPanel.classList.contains('active')) return;
      if (!this.navRoot) return;
      if (!this.navRoot.contains(e.target)) this.closeAllSubmenus();
    });

    // Focus leaving the nav also closes (keyboard users)
    document.addEventListener('focusin', (e) => {
      if (!this.isDesktop()) return;
      if (!this.submenuPanel || !this.submenuPanel.classList.contains('active')) return;
      if (!this.navRoot) return;
      if (!this.navRoot.contains(e.target)) this.closeAllSubmenus();
    });
  }

  openSubmenu(btn, opts = {}) {
    const { focusPanel = false } = opts;
    const targetId = btn.getAttribute('aria-controls');
    const target   = targetId ? document.getElementById(targetId) : null;
    if (!target) return;

    this.submenuSections.forEach((sec) => {
      sec.hidden = true;
      sec.setAttribute('hidden', '');
    });
    this.mainMenu.querySelectorAll('.mega-nav__menu-button').forEach((b) => b.setAttribute('aria-expanded','false'));

    if (this._submenuCloseTimer) {
      clearTimeout(this._submenuCloseTimer);
      this._submenuCloseTimer = null;
    }
    if (this.submenuPanel && this._submenuClosingHandler) {
      this.submenuPanel.removeEventListener('transitionend', this._submenuClosingHandler);
      this._submenuClosingHandler = null;
    }

    btn.setAttribute('aria-expanded','true');
    target.hidden = false;
    target.removeAttribute('hidden');
    this.activeBtn = btn;
    this._pendingReturnFocus = null;

    // Wohnmobile: auf Desktop immer „sichtbaren“ Default haben
    if (target && target === this.getWohnmobileSection() && this.isDesktop()) {
      this.activateBaureihe(this._wmActiveBaureihe || 'vollintegriert', { autoModel: true });
    }

    if (this.submenuPanel) {
      this.submenuPanel.classList.remove('closing');
      this.submenuPanel.classList.add('active');
      const submenuKey = target.getAttribute('data-submenu') || '';
      const submenuType = target.getAttribute('data-submenu-type') || '';
      const hasDarkCta = target.getAttribute('data-cta-dark') === '1';
      if (submenuKey) {
        this.submenuPanel.setAttribute('data-submenu-open', submenuKey);
      } else {
        this.submenuPanel.removeAttribute('data-submenu-open');
      }
      if (submenuType) {
        this.submenuPanel.setAttribute('data-submenu-type-open', submenuType);
      } else {
        this.submenuPanel.removeAttribute('data-submenu-type-open');
      }
      if (hasDarkCta) {
        this.submenuPanel.setAttribute('data-cta-dark', '1');
      } else {
        this.submenuPanel.removeAttribute('data-cta-dark');
      }
    }
    const header = document.querySelector('.site-header');
    const hasStickyHeader = this.isDesktop() && header && header.classList.contains('sticky');
    document.body.classList.add('menu-open');
    document.body.classList.toggle('menu-open--sticky', !!hasStickyHeader);
    // On keyboard open, move focus into the panel (first interactive element)
    if (focusPanel) {
      // Schedule focus for the next frame to ensure DOM/state (e.g., activateBaureihe) is fully applied
      requestAnimationFrame(() => this._focusFirstIn(target));
    }
  }

  closeAllSubmenus() {
    const returnTo = this.activeBtn || null;
    if (returnTo && !this._pendingReturnFocus) this._pendingReturnFocus = returnTo;

    if (this.mainMenu) {
      this.mainMenu.querySelectorAll('.mega-nav__menu-button').forEach((b) => b.setAttribute('aria-expanded','false'));
    }

    const panel = this.submenuPanel;
    const allowAnimation = panel && this.isDesktop();

    const finalize = () => {
      this.submenuSections.forEach((sec) => {
        sec.hidden = true;
        sec.setAttribute('hidden', '');
      });

      if (panel) {
        panel.classList.remove('closing');
        panel.classList.remove('active');
        panel.removeAttribute('data-submenu-open');
        panel.removeAttribute('data-submenu-type-open');
        panel.removeAttribute('data-cta-dark');
        if (this._submenuClosingHandler) {
          panel.removeEventListener('transitionend', this._submenuClosingHandler);
          this._submenuClosingHandler = null;
        }
      }

      if (this._submenuCloseTimer) {
        clearTimeout(this._submenuCloseTimer);
        this._submenuCloseTimer = null;
      }

      if (!this.isMobileMenuOpen) {
        document.body.classList.remove('menu-open');
      }
      document.body.classList.remove('menu-open--sticky');

      const focusTarget = this._pendingReturnFocus;
      this._pendingReturnFocus = null;
      this.activeBtn = null;
      if (focusTarget && typeof focusTarget.focus === 'function') focusTarget.focus();
    };

    if (allowAnimation && panel.classList.contains('active') && !panel.classList.contains('closing')) {
      const handler = (e) => {
        if (e && e.target !== panel) return;
        finalize();
      };
      this._submenuClosingHandler = handler;
      panel.addEventListener('transitionend', handler, { once: true });
      // allow fade-out to play by keeping panel visible
      panel.classList.add('closing');

      if (this._submenuCloseTimer) clearTimeout(this._submenuCloseTimer);
      this._submenuCloseTimer = setTimeout(() => finalize(), 350);
      return;
    }

    // If already closing or inactive, finish immediately
    if (allowAnimation && panel.classList.contains('closing')) return;

    finalize();
  }

  _focusFirstIn(root) {
    if (!root) return;
    // Prefer Wohnmobile tabs first, then models, then generic anchors/buttons
    const selectorPriority = [
      '.wm-tabs [role="tab"][aria-selected="true"]',
      '.wm-tabs [role="tab"]',
      '.wm-model[data-model]',
      'a[href]',
      'button',
      'input', 'select', 'textarea',
      '[tabindex]:not([tabindex="-1"])'
    ];

    const ensureFocusableAndFocus = (el) => {
      if (!el) return false;
      const isNaturallyFocusable = /^(A|BUTTON|INPUT|SELECT|TEXTAREA)$/.test(el.tagName);
      const hasTabIndex = el.hasAttribute('tabindex');
      if (!isNaturallyFocusable && (!hasTabIndex || el.tabIndex < 0)) {
        el.setAttribute('tabindex', '-1');
      }
      if (typeof el.focus === 'function') { el.focus(); return true; }
      return false;
    };

    const tryOnce = () => {
      for (const sel of selectorPriority) {
        const candidate = root.querySelector(sel);
        if (candidate && ensureFocusableAndFocus(candidate)) return true;
      }
      return false;
    };

    if (tryOnce()) return;
    // Retry once on the next frame in case content just toggled from [hidden]
    requestAnimationFrame(() => {
      if (tryOnce()) return;
      // Final fallback: focus the panel itself so focus does not escape to body
      ensureFocusableAndFocus(root);
    });
  }

  // =========================
  _desktopModeIs(mode) {
    return DESKTOP_TRIGGER_MODE === mode;
  }
  // Mobile portal helpers
  // =========================
  _moveSectionToMobile(menuKey) {
    const section = document.querySelector(`[data-submenu="${menuKey}"]`);
    if (!section) return null;

    if (!this._placeholders.has(menuKey)) {
      const placeholder = document.createComment(`submenu-placeholder:${menuKey}`);
      this._placeholders.set(menuKey, placeholder);
      this._originalParents.set(menuKey, section.parentNode);
    }
    // Ensure placeholder is in place of the section before moving it
    const placeholder = this._placeholders.get(menuKey);
    if (section.parentNode) {
      section.parentNode.replaceChild(placeholder, section);
    }

    // Mark as mobile variant and return the node to be appended
    section.classList.add('mega-nav__section--mobile');
    section.hidden = false;
    section.removeAttribute('hidden');

    // In der mobilen Darstellung benötigen wir keine aktive Modell-Markierung
    section.querySelectorAll('.wm-model.is-active').forEach((btn) => btn.classList.remove('is-active'));
    this._currentMobileKey = menuKey;
    return section;
  }

  _restoreSection(menuKey) {
    const section = document.querySelector(`[data-submenu="${menuKey}"]`);
    const placeholder = this._placeholders.get(menuKey);
    const parent = this._originalParents.get(menuKey);
    if (section && placeholder && parent && placeholder.parentNode) {
      section.classList.remove('mega-nav__section--mobile');
      parent.replaceChild(section, placeholder);
      section.hidden = true;
    }
    if (this._currentMobileKey === menuKey) this._currentMobileKey = null;
  }

  _restoreAnyMobileSection() {
    if (!this._currentMobileKey) return;
    this._restoreSection(this._currentMobileKey);
  }

  // =========================
  // Wohnmobile (tabs + models)
  // =========================
  initWohnmobile() {
    this.wmSection = this.getWohnmobileSection();
    if (!this.wmSection) return;

    // Tab clicks (vollintegriert / teilintegriert)
    const tabs = this.wmSection.querySelector('.wm-tabs');
    if (tabs) {
      tabs.addEventListener('click', (e) => {
        const btn = e.target.closest('[role="tab"][data-wm-tab]');
        if (!btn) return;
        e.preventDefault();
        this.activateBaureihe(btn.getAttribute('data-wm-tab'), { autoModel: this.isDesktop() });
      });

      tabs.addEventListener('keydown', (e) => {
        if (!this.isDesktop()) return;
        const btn = e.target.closest('[role="tab"][data-wm-tab]');
        if (!btn || !tabs.contains(btn)) return;

        if (e.key === 'ArrowDown') {
          e.preventDefault();
          e.stopPropagation();

          const tabKey = btn.getAttribute('data-wm-tab');
          this.activateBaureihe(tabKey, { autoModel: true });

          requestAnimationFrame(() => {
            const panel = this.wmSection
              ? this.wmSection.querySelector(`.wm-panel[role="tabpanel"][data-wm-panel="${tabKey}"]`)
              : null;
            if (panel) this._focusFirstIn(panel);
          });
        }
      });
    }

    // Model clicks (delegate)
    this.wmSection.addEventListener('click', (e) => {
      const modelBtn = e.target.closest('.wm-model[data-model]');
      if (!modelBtn || !this.wmSection.contains(modelBtn)) return;
      e.preventDefault();
      this.activateModel(modelBtn.getAttribute('data-model'));
    });

    // Initial state: vollintegriert
    // Desktop: erstes Modell automatisch; Mobile: keine Autoauswahl
    this.activateBaureihe('vollintegriert', { autoModel: this.isDesktop() });
  }

  activateBaureihe(key, opts = {}) {
    if (!this.wmSection) return;
    const { autoModel = false } = opts;

    // Tabs aria-selected
    this.wmSection.querySelectorAll('.wm-tabs [role="tab"]').forEach(tab => {
      const isActive = tab.getAttribute('data-wm-tab') === key;
      tab.setAttribute('aria-selected', String(isActive));
    });

    // Panels show/hide (Desktop fades entire grid, Mobile instant)
    const panels = Array.from(this.wmSection.querySelectorAll('.wm-panel[role="tabpanel"]'));
    panels.forEach((panelEl) => {
      const panelKey = panelEl.getAttribute('data-wm-panel');
      const match = panelKey === key;

      if (this.isDesktop()) {
        if (match) {
          if (panelEl.hidden) {
            panelEl.classList.remove('fade-in');
            panelEl.classList.add('fade-prep');
            panelEl.hidden = false;
            // Force style flush so transition can run
            // eslint-disable-next-line no-unused-expressions
            void panelEl.offsetWidth;
            panelEl.classList.add('fade-in');
            const cleanupPanel = () => {
              panelEl.classList.remove('fade-prep', 'fade-in');
              panelEl.removeEventListener('transitionend', cleanupPanel);
            };
            panelEl.addEventListener('transitionend', cleanupPanel, { once: true });
          }
        } else {
          panelEl.classList.remove('fade-prep', 'fade-in');
          panelEl.hidden = true;
        }
      } else {
        // Mobile: original instant show/hide
        panelEl.classList.remove('fade-prep', 'fade-in');
        panelEl.hidden = !match;
      }
    });

    // Remember current baureihe
    this._wmActiveBaureihe = key;

    // Reset model active states in active panel
    const activePanel = this.wmSection.querySelector(`.wm-panel[role="tabpanel"][data-wm-panel="${key}"]`);
    if (!activePanel) return;

    if (this.isDesktop()) {
      this._dockLinksForDesktop(activePanel);
    }

    activePanel.querySelectorAll('.wm-model').forEach(b => b.classList.remove('is-active'));
    activePanel.querySelectorAll('.wm-model-panel[data-model-panel]').forEach(panelEl => {
      if (this.isDesktop()) {
        panelEl.hidden = true;
      } else {
        panelEl.hidden = false;
      }
      panelEl.classList.remove('fade-prep', 'fade-in');
    });

    this._updateModelPanelsSlider(activePanel, null);

    if (autoModel) {
      // Always pick the first model in the visible list (no hardcoded preference)
      const first = activePanel.querySelector('.wm-model[data-model]');
      if (first) this.activateModel(first.getAttribute('data-model'));
    }
  }

  activateModel(modelKey) {
    if (!this.wmSection || !this._wmActiveBaureihe) return;
    const panel = this.wmSection.querySelector(`.wm-panel[role="tabpanel"][data-wm-panel="${this._wmActiveBaureihe}"]`);
    if (!panel) return;

    // Active button styling (class only; typography handled elsewhere)
    const isDesktop = this.isDesktop();
    panel.querySelectorAll('.wm-model').forEach((btn) => {
      const match = btn.getAttribute('data-model') === modelKey;
      if (isDesktop) {
        btn.classList.toggle('is-active', match);
      } else {
        btn.classList.remove('is-active');
      }
    });

    // Show matching detail/meta columns; hide others
    const modelPanels = Array.from(panel.querySelectorAll('.wm-model-panel[data-model-panel]'));
    modelPanels.forEach((el) => {
      const match = el.getAttribute('data-model-panel') === modelKey;

      if (this.isDesktop()) {
        // Desktop: animate hidden → visible with fade classes; keep others fully out of layout
        if (match) {
          if (el.hidden) {
            // Prepare: start from invisible state (CSS .fade-prep sets opacity/transform)
            el.classList.remove('fade-in');
            el.classList.add('fade-prep');
            // Make it participate in layout, so transition can run
            el.hidden = false;
            // Force style flush so transition runs from 0 → 1
            // eslint-disable-next-line no-unused-expressions
            void el.offsetWidth;
            // Trigger the fade-in
            el.classList.add('fade-in');
            const cleanup = () => {
              el.classList.remove('fade-prep', 'fade-in');
              el.removeEventListener('transitionend', cleanup);
            };
            el.addEventListener('transitionend', cleanup, { once: true });
          }
        } else {
          // Hide non-matching panels immediately and reset classes
          el.classList.remove('fade-prep', 'fade-in');
          el.hidden = true;
        }
      } else {
        // Mobile: leave all slides sichtbar, Swiper übernimmt die Anzeige
        el.classList.remove('fade-prep', 'fade-in');
        el.hidden = false;
      }
    });

    this._updateModelPanelsSlider(panel, modelKey);

    // In mobile, the section ist portaled und stacked ...
  }

  // ----------------------------------------------------------------
  // Ensure the two mobile panes for Wohnmobile exist (idempotent)
  // ----------------------------------------------------------------
  _ensureMobileWohnmobilePanes() {
    if (!this.mobileSubmenu) return { listPane: null, detailPane: null };
    let listPane   = document.getElementById('wmMobileListPane');
    let detailPane = document.getElementById('wmMobileDetailPane');

    if (!listPane) {
      listPane = document.createElement('div');
      listPane.id = 'wmMobileListPane';
      listPane.className = 'wm-mobile-list';
      this.mobileSubmenu.appendChild(listPane);
    }
    if (!detailPane) {
      detailPane = document.createElement('div');
      detailPane.id = 'wmMobileDetailPane';
      detailPane.className = 'wm-mobile-detail';
      this.mobileSubmenu.appendChild(detailPane);
    }
    return { listPane, detailPane };
  }

  _resetMobileOverlayScroll() {
    const scrollRoot = this.mobileMenuOverlay?.querySelector('.mega-nav__overlay-content');
    [scrollRoot, this.mobileSubmenu].filter(Boolean).forEach((el) => {
      el.scrollTop = 0;
      if (typeof el.scrollTo === 'function') {
        el.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      }
    });
  }

  _applyMobileListPadding(el, opts = {}) {
    if (!el) return;
    const { track = false } = opts;
    this._mobileListPadding.forEach((cls) => el.classList.add(cls));
    if (track) this._mobilePaddedElements.add(el);
  }

  _removeMobileListPadding(el) {
    if (!el) return;
    this._mobileListPadding.forEach((cls) => el.classList.remove(cls));
    this._mobilePaddedElements.delete(el);
  }

  // =========================
  // Mobile 3. Ebene (Wohnmobile)
  // =========================
  _ensureMobileBreadcrumb() {
    if (!this.mobileSubmenu) return null;
    let crumb = this.mobileSubmenu.querySelector('.mega-nav__breadcrumb');
    if (!crumb) {
      crumb = document.createElement('div');
      crumb.className = 'mega-nav__breadcrumb px-8 md:px-15 xl:px-0';
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'mega-nav__breadcrumb-btn';
      btn.setAttribute('aria-label', 'Zurück');
      btn.appendChild(createSpriteIcon('arrow-right', 'mega-nav__breadcrumb-icon', 16, 16, '0 0 14 21'));
      const span = document.createElement('span');
      btn.appendChild(span);
      crumb.appendChild(btn);
      this.mobileSubmenu.prepend(crumb);
    }
    return crumb;
  }

  _setMobileBreadcrumb(label, onBack) {
    const crumb = this._ensureMobileBreadcrumb();
    if (!crumb) return;

    // Clear any previous content to avoid leftover focusable elements
    crumb.innerHTML = '';

    if (typeof onBack === 'function') {
      // Interactive breadcrumb (has back action)
      const btn  = document.createElement('button');
      btn.type   = 'button';
      btn.className = 'mega-nav__breadcrumb-btn';
      btn.setAttribute('aria-label', 'Zurück');

      btn.appendChild(createSpriteIcon('arrow-right', 'mega-nav__breadcrumb-icon', 16, 16, '0 0 14 21'));
      const span = document.createElement('span');
      span.textContent = label || 'Menü';
      btn.appendChild(span);

      btn.addEventListener('click', (e) => {
        e.preventDefault();
        onBack();
      }, { once: true });

      crumb.appendChild(btn);
    } else {
      // Non-interactive label variant (no focusable element)
      const div = document.createElement('div');
      div.className = 'mega-nav__breadcrumb-label';
      div.textContent = label || 'Menü';
      // Ensure it does not take focus in the trap
      div.setAttribute('tabindex', '-1');
      crumb.appendChild(div);
    }
  }

  _getActiveWohnmobilePanel() {
    if (!this.wmSection) return null;
    const key = this._wmActiveBaureihe || 'vollintegriert';
    return this.wmSection.querySelector(`.wm-panel[role="tabpanel"][data-wm-panel="${key}"]`);
  }

  _buildMobileWohnmobileList(section) {
    let listPane   = document.getElementById('wmMobileListPane');
    let detailPane = document.getElementById('wmMobileDetailPane');

    // Recreate panes if they were removed by another submenu
    if (!listPane || !detailPane) {
      const ensured = this._ensureMobileWohnmobilePanes();
      listPane   = ensured.listPane;
      detailPane = ensured.detailPane;
      if (!listPane || !detailPane) return; // give up safely
    }

    // Ensure links are back in the section before we clear the listPane (so we don't lose the node)
    this._restoreLinks(section);

    // Clear panes
    listPane.innerHTML = '';
    detailPane.innerHTML = '';

    const activePanel = this._getActiveWohnmobilePanel() || section.querySelector('.wm-panel');
    if (!activePanel) return;

    // Back to list-level: ensure section is NOT in detail mode and is parked off-canvas
    section.classList.remove('is-mobile-detail');
    if (detailPane && section.parentNode !== detailPane) {
      detailPane.appendChild(section); // stays off-canvas
    }

    // 1) Add "Alle Wohnmobile" link (clone ist hier okay)
    const allLink = section.querySelector('#nav-alle-wohnmobile');
    if (allLink) {
      const wrapper = document.createElement('div');
      wrapper.className = 'wm-all';
      wrapper.appendChild(allLink.cloneNode(true));
      this._applyMobileListPadding(wrapper);
      listPane.appendChild(wrapper);
    }

    // 1b) Für Mobile: Überschriften der Baureihen ausgeben und JEWEILS die echte Models-Aside darunter teleportieren
    this._restoreAllModelAsides(); // safety

    const wmTabs = section.querySelector('.wm-tabs');
    const order = ['vollintegriert', 'teilintegriert'];

    order.forEach((key) => {
      // Label aus Desktop-Tab holen (Fallback-Texte, falls Tab fehlt)
      const tabBtn = wmTabs ? wmTabs.querySelector(`[role="tab"][data-wm-tab="${key}"]`) : null;
      const label  = tabBtn
        ? tabBtn.textContent.trim()
        : (key === 'vollintegriert' ? 'Vollintegrierte Wohnmobile' : 'Teilintegrierte Wohnmobile');

      const heading = document.createElement('div');
      heading.className = 'wm-link wm-link--heading';
      heading.setAttribute('role', 'heading');
      heading.setAttribute('aria-level', '2');
      heading.textContent = label;
      this._applyMobileListPadding(heading);
      listPane.appendChild(heading);

      // Passende Models-Aside unter die Überschrift setzen (Teleport, kein Clone)
      this._moveModelsAsideToList(section, listPane, key);

      // Nach dem Inhalt von "Teilintegriert": gemeinsame Linkliste **einmalig** unter die Listen setzen (Teleport, kein Clone)
      if (key === 'teilintegriert') this._moveLinksToList(section, listPane);
    });


    // Breadcrumb zeigt die VORIGE Ebene (Overlay-Hauptliste)
    this._setMobileBreadcrumb('Wohnmobile', () => {
      this.showMobileMain();
    });

    // Reset Pane-States – Ebene 2 sichtbar
    listPane.classList.remove('slide-out', 'hidden');
    detailPane.classList.remove('slide-in');

    // (Re)bind Tap-Handler robust, falls listPane neu erstellt wurde (z. B. nach Wechsel von "Neuheiten" zurück)
    // Entferne alten Listener vom vorherigen Pane
    if (this._wmListEl && this._wmListClickHandler) {
      this._wmListEl.removeEventListener('click', this._wmListClickHandler);
    }
    // Erzeuge (einmal) den delegierten Handler und binde ihn am aktuellen listPane
    if (!this._wmListClickHandler) {
      this._wmListClickHandler = (e) => {
        const currentListPane = document.getElementById('wmMobileListPane');
        const modelBtn = e.target.closest('.wm-model[data-model]');
        if (!modelBtn || !currentListPane || !currentListPane.contains(modelBtn)) return;
        e.preventDefault();

        const modelsAside = modelBtn.closest('.wm-col--models');
        const baureiheKey = modelsAside?.dataset.mobileBaureihe;
        if (baureiheKey && baureiheKey !== this._wmActiveBaureihe) {
          this.activateBaureihe(baureiheKey, { autoModel: false });
        }

        const modelKey = modelBtn.getAttribute('data-model');
        this.activateModel(modelKey);
        this._showMobileDetailForModel(modelKey, section);
      };
    }
    listPane.addEventListener('click', this._wmListClickHandler);
    this._wmListEl = listPane;
  }

  _showMobileDetailForModel(modelKey, section) {
    const listPane   = document.getElementById('wmMobileListPane');
    const detailPane = document.getElementById('wmMobileDetailPane');
    if (!listPane || !detailPane) return;

    // section enthält bereits die echten Inhalte (portaled)
    detailPane.innerHTML = '';

    // Ensure models-aside is back inside the section before showing detail
    // WICHTIG: Links NICHT zurückrestoren – sie sollen nur in Ebene 2 sichtbar sein
    this._restoreModelsAside(section);
    // this._restoreLinks(section); // intentionally disabled to keep links in Level 2

    detailPane.appendChild(section);
    section.classList.add('is-mobile-detail');

    const activeDetailPanel = section.querySelector(
      `.wm-panel[role="tabpanel"][data-wm-panel="${this._wmActiveBaureihe || 'vollintegriert'}"]`
    );
    this._updateModelPanelsSlider(activeDetailPanel, modelKey);

    // Ebene 2: statt verstecken → mit Slide-Out animiert herausfahren
    listPane.classList.add('slide-out');
    this._resetMobileOverlayScroll();
    detailPane.classList.add('slide-in');
    requestAnimationFrame(() => this._resetMobileOverlayScroll());

    // Breadcrumb zeigt die VORIGE Ebene (Ebene 2: Baureihe-Liste)
    const tabBtn = this.wmSection?.querySelector(`.wm-tabs [data-wm-tab="${this._wmActiveBaureihe || 'vollintegriert'}"]`);
    const prevLabel = tabBtn ? tabBtn.textContent.trim() : 'Wohnmobile';
    this._setMobileBreadcrumb(prevLabel, () => {
      // Zur Ebene 2: Liste neu aufbauen (Breadcrumb dort zeigt auf „Menü“)
      this._buildMobileWohnmobileList(section);
    });
  }

  // =========================
  // Mobile overlay
  // =========================
  setupMobileMenu() {
    this.mobileMenuButton && this.mobileMenuButton.addEventListener('click', () => this.openMobileMenu());
    this.closeButton && this.closeButton.addEventListener('click', () => this.closeMobileMenu());
    this.mobileMenuOverlay && this.mobileMenuOverlay.addEventListener('click', (e) => {
      if (e.target === this.mobileMenuOverlay) this.closeMobileMenu();
    });
  }

  openMobileMenu() {
    if (!this.mobileMenuOverlay) return;
    this.prevFocused = document.activeElement;

    this.renderMobileMainFromDesktop();
    // Main (Ebene 1) per Slide-In zeigen – robuste Sequenz
    // 1) Sämtliche alten Transition-Klassen entfernen
    this.mobileMainMenu.classList.remove('slide-in', 'slide-out');
    this.mobileSubmenu.classList.remove('slide-in');

    // 2) Reflow erzwingen, damit der Startzustand (#mobileMainMenu { transform: translateX(100%); }) greift
    // eslint-disable-next-line no-unused-expressions
    void this.mobileMainMenu.offsetWidth;

    // 3) Nächsten Frame: slide-in hinzufügen (garantiert, dass die Transition startet)
    requestAnimationFrame(() => {
      this.mobileMainMenu.classList.add('slide-in');
    });

    this.mobileMenuOverlay.hidden = false;
    // Make overlay programmatically focusable for trap fallback
    this.mobileMenuOverlay.setAttribute('tabindex', '-1');
    document.body.classList.add('menu-open');
    document.body.classList.remove('menu-open--sticky');
    this.isMobileMenuOpen = true;

    const first = this.mobileMenuOverlay.querySelector('button, [href], [tabindex]:not([tabindex="-1"])');
    if (first) first.focus();
    this.installFocusTrap();
  }

  closeMobileMenu() {
    if (!this.mobileMenuOverlay) return;

    // Helper: finalize close after animation ends (or fallback timeout)
    const finalizeClose = () => {
      // Ensure teleported model asides are restored before putting sections back
      this._restoreAllModelAsides();
      // Restore links if necessary
      this._restoreLinks(this.getWohnmobileSection());

      this.uninstallFocusTrap();
      this._restoreAnyMobileSection();

      // Clear mobile panes (3. Ebene)
      const listPane   = document.getElementById('wmMobileListPane');
      const detailPane = document.getElementById('wmMobileDetailPane');
      if (listPane) listPane.innerHTML = '';
      if (detailPane) detailPane.innerHTML = '';

      this.mobileMenuOverlay.hidden = true;
      document.body.classList.remove('menu-open');
      document.body.classList.remove('menu-open--sticky');
      this.isMobileMenuOpen = false;

      // Remove temporary tabindex from overlay
      if (this.mobileMenuOverlay) this.mobileMenuOverlay.removeAttribute('tabindex');

      // Reset classes & markup for next open
      if (this.mobileMainMenu) {
        this.mobileMainMenu.classList.remove('slide-in', 'slide-out', 'slide-out-close');
        this.mobileMainMenu.innerHTML = '';
      }
      if (this.mobileSubmenu) {
        this.mobileSubmenu.classList.remove('slide-in', 'slide-out-close');
        this.mobileSubmenu.innerHTML  = '';
      }

      if (this.prevFocused && typeof this.prevFocused.focus === 'function') this.prevFocused.focus();
    };

    // Animate only the currently visible pane to keep timing consistent
    const panes = [];
    const mainIsVisible = this.mobileMainMenu && this.mobileMainMenu.classList.contains('slide-in') && !this.mobileMainMenu.classList.contains('slide-out-close');
    const subIsVisible  = this.mobileSubmenu && this.mobileSubmenu.classList.contains('slide-in') && !this.mobileSubmenu.classList.contains('slide-out-close');

    if (subIsVisible) {
      panes.push(this.mobileSubmenu);
    } else if (mainIsVisible) {
      panes.push(this.mobileMainMenu);
    }

    if (panes.length) {
      let remaining = panes.length;
      const onEnd = (el, handler) => {
        el.removeEventListener('transitionend', handler);
        remaining -= 1;
        if (remaining <= 0) finalizeClose();
      };

      // Start animations: keep any existing 'slide-in' (so transform starts at 0), then add 'slide-out-close'
      panes.forEach((el) => {
        // eslint-disable-next-line no-unused-expressions
        void el.offsetWidth; // commit current style
        const handler = (e) => {
          if (e && e.target !== el) return; // only count events on the element itself
          onEnd(el, handler);
        };
        el.addEventListener('transitionend', handler, { once: true });
        el.classList.add('slide-out-close');
      });

      // Fallback timeout if no transitionend fires (missing CSS or 0 duration)
      this._closeFallbackTimer && clearTimeout(this._closeFallbackTimer);
      this._closeFallbackTimer = setTimeout(() => finalizeClose(), 600);
      return; // wait for animations
    }

    // If nothing to animate, close immediately
    finalizeClose();
  }

  renderMobileMainFromDesktop() {
    if (!this.mobileMainMenu || !this.mainMenu) return;
    this.mobileMainMenu.innerHTML = '';

    const triggers = Array.from(this.mainMenu.querySelectorAll('.mega-nav__menu-button'));
    triggers.forEach((btn) => {
      const key = btn.id.replace(/^btn-/, '');
      const item = document.createElement('button');
      item.className = 'mega-nav__overlay-main-item has-chevron px-8 md:px-15 xl:px-0';
      item.dataset.mobileMenu = key;
      const label = document.createElement('span');
      label.textContent = btn.textContent.trim();
      item.appendChild(label);
      item.appendChild(createSpriteIcon('arrow-right', 'mega-nav__chevron-icon', 16, 16, '0 0 14 21'));
      item.addEventListener('click', () => this.openMobileSubmenu(key));
      this.mobileMainMenu.appendChild(item);
    });

    this.mobileMainMenu.classList.remove('slide-out');
    this.mobileSubmenu.classList.remove('slide-in');
  }

  openMobileSubmenu(menuKey) {
    if (!this.mobileSubmenu || !this.mobileMainMenu) return;

    // Ensure any previously portaled section is restored before moving a new one
    this._restoreAnyMobileSection();

    // Für Wohnmobile: Pane-Container bleiben erhalten
    if (menuKey === this.getWohnmobileMenuKey()) {
      const section = document.querySelector(`[data-submenu="${menuKey}"]`);
      if (!section) return;

      // Portalen
      const moved = this._moveSectionToMobile(menuKey);

      // Ensure panes exist (in case another submenu cleared the overlay)
      const { detailPane } = this._ensureMobileWohnmobilePanes();
      // Park section initially in the detail pane (off-canvas) so it never floats as a sibling
      if (moved && detailPane && moved.parentNode !== detailPane) {
        detailPane.appendChild(moved);
      }

      // Ensure breadcrumb exists and build list pane
      this._ensureMobileBreadcrumb();
      this.activateBaureihe('vollintegriert', { autoModel: false }); // Mobile: keine Autoauswahl
      if (moved) this._buildMobileWohnmobileList(moved);

      // Animate Overlay: main → out, sub → in
      this.mobileMainMenu.classList.add('slide-out');
      this.mobileSubmenu.classList.add('slide-in');
      return;
    }

    // ---- Standard-Fall für andere Menüs ----
    this.mobileSubmenu.innerHTML = '';

    const section = document.querySelector(`[data-submenu="${menuKey}"]`);
    if (!section) return;

    const wrap = document.createElement('div');
    wrap.className = 'mobile-submenu-content active';
    wrap.dataset.mobileSubmenu = menuKey;

    // Breadcrumb
    const breadcrumb = document.createElement('div');
    breadcrumb.className = 'mega-nav__breadcrumb px-8 md:px-15 xl:px-0';

    const crumbBtn = document.createElement('button');
    crumbBtn.type = 'button';
    crumbBtn.className = 'mega-nav__breadcrumb-btn';
    crumbBtn.setAttribute('aria-label', 'Zurück');
    crumbBtn.addEventListener('click', (e) => { e.preventDefault(); this.showMobileMain(); });
    crumbBtn.appendChild(createSpriteIcon('arrow-right', 'mega-nav__breadcrumb-icon', 16, 16, '0 0 14 21'));
    const crumbLabel = document.createElement('span');
    crumbLabel.textContent = this.getMenuLabel(menuKey);
    crumbBtn.appendChild(crumbLabel);
    breadcrumb.appendChild(crumbBtn);
    wrap.appendChild(breadcrumb);

    const moved = this._moveSectionToMobile(menuKey);
    if (moved) wrap.appendChild(moved);

    this.mobileSubmenu.appendChild(wrap);

    this.mobileMainMenu.classList.add('slide-out');
    this.mobileSubmenu.classList.add('slide-in');
  }

  showMobileMain() {
    // Put back any teleported model asides & links (so the section is complete again)
    this._restoreAllModelAsides();
    this._restoreLinks(this.getWohnmobileSection());
    this._restoreAnyMobileSection();
    this.renderMobileMainFromDesktop();

    // After rebuilding Level 1, move focus to the first actionable item
    const firstMain = this.mobileMainMenu && this.mobileMainMenu.querySelector('.mega-nav__overlay-main-item, button, [href], [tabindex]:not([tabindex="-1"])');
    if (firstMain) firstMain.focus();

    // Sicherheit: Detail-Zustand entfernen
    const wmSection = this.getWohnmobileSection();
    if (wmSection) wmSection.classList.remove('is-mobile-detail');

    // 3. Ebene leeren & Breadcrumb resetten
    const listPane   = document.getElementById('wmMobileListPane');
    const detailPane = document.getElementById('wmMobileDetailPane');
    if (listPane) listPane.innerHTML = '';
    if (detailPane) detailPane.innerHTML = '';
    this._setMobileBreadcrumb('Menü', null);

    this.mobileMainMenu.classList.remove('slide-out');
    this.mobileSubmenu.classList.remove('slide-in');
  }

  // =========================
  // Wohnmobile: teleport models aside (no cloning)
  // =========================
  _getModelsAside(section, key = null) {
    // find active panel or the one for 'key'
    let panel = null;
    if (key) {
      panel = section.querySelector(`.wm-panel[role="tabpanel"][data-wm-panel="${key}"]`);
    }
    if (!panel) {
      const activeKey = this._wmActiveBaureihe || 'vollintegriert';
      panel = section.querySelector(`.wm-panel[role="tabpanel"][data-wm-panel="${activeKey}"]`);
    }
    if (!panel) return null;
    return panel.querySelector('.wm-col--models');
  }

  _moveModelsAsideToList(section, listPane, key = null) {
    const aside = this._getModelsAside(section, key);
    if (!aside || !listPane) return;

    if (key) {
      aside.dataset.mobileBaureihe = key;
    }

    // Ensure we have a placeholder and remember original parent
    if (!this._wmAsidePlaceholders.has(aside)) {
      const placeholder = document.createComment('wm-models-aside-placeholder');
      const parent = aside.parentNode;
      if (parent) {
        parent.replaceChild(placeholder, aside);
        this._wmAsidePlaceholders.set(aside, { placeholder, parent });
      }
    }
    // Append the original aside into the list pane (teleport)
    listPane.appendChild(aside);
    this._applyMobileListPadding(aside, { track: true });
  }

  _restoreModelsAside(section) {
    // Restore any asides we have moved from this section
    for (const [aside, ref] of this._wmAsidePlaceholders.entries()) {
      const { placeholder, parent } = ref || {};
      if (placeholder && parent && placeholder.parentNode) {
        this._removeMobileListPadding(aside);
        parent.replaceChild(aside, placeholder);
      }
      this._wmAsidePlaceholders.delete(aside);
    }
  }

  _restoreAllModelAsides() {
    // Global safety to put back any moved model-asides (across baureihen)
    for (const [aside, ref] of this._wmAsidePlaceholders.entries()) {
      const { placeholder, parent } = ref || {};
      if (placeholder && parent && placeholder.parentNode) {
        this._removeMobileListPadding(aside);
        parent.replaceChild(aside, placeholder);
      }
      this._wmAsidePlaceholders.delete(aside);
    }
  }

  // =========================
  // Wohnmobile: teleport shared link list (.wm-links)
  // =========================
  _moveLinksToList(section, listPane) {
    const links =
      section.querySelector('.wm-links') ||
      listPane.querySelector('.wm-links') ||
      this._wmLinksEl;
    if (!links || !listPane) return;

    // Remember the element so we can restore it later even if it's no longer in the section
    this._wmLinksEl = links;

    links.classList.remove('wm-links--docked');

    if (!this._wmLinksPlaceholder) {
      this._wmLinksPlaceholder = document.createComment('wm-links-placeholder');
      // If links are still inside the section, swap them with placeholder
      if (links.parentNode && section.contains(links)) {
        links.parentNode.replaceChild(this._wmLinksPlaceholder, links);
      } else if (!section.contains(this._wmLinksPlaceholder)) {
        // Ensure placeholder is inside the section if it isn't already
        const anchor = section.querySelector('.wm-text, .wm-panels, .wm-tabs, .wm-grid, .wm-panel:last-child');
        (anchor && anchor.parentNode ? anchor.parentNode : section).appendChild(this._wmLinksPlaceholder);
      }
    }

    // Teleport original element directly after the last models column.
    // This keeps the mobile order stable even if the node was previously docked/restored.
    const modelAsides = listPane.querySelectorAll('.wm-col--models');
    const lastModelsAside = modelAsides.length ? modelAsides[modelAsides.length - 1] : null;

    if (lastModelsAside && lastModelsAside.parentNode === listPane) {
      lastModelsAside.insertAdjacentElement('afterend', links);
    } else {
      listPane.appendChild(links);
    }

    this._applyMobileListPadding(links, { track: true });
  }

  _restoreLinks(section) {
    if (!section) return;
    const placeholder = this._wmLinksPlaceholder;
    // Prefer stored reference; otherwise query anywhere in the DOM
    const links = this._wmLinksEl || document.querySelector('#wmMobileListPane .wm-links, .wm-links');

    if (placeholder && placeholder.parentNode && links) {
      links.classList.remove('wm-links--docked');
      this._removeMobileListPadding(links);
      placeholder.parentNode.replaceChild(links, placeholder);
      this._wmLinksPlaceholder = null;
      this._wmLinksEl = links; // keep reference
    }
  }

  _dockLinksForDesktop(panel) {
    if (!panel || !this.isDesktop()) return;
    const section = this.wmSection;
    if (!section) return;

    const links = section.querySelector('.wm-links');
    if (!links) return;

    const modelsAside = panel.querySelector('.wm-col--models');
    if (!modelsAside) return;

    this._wmLinksEl = links;

    if (!this._wmLinksPlaceholder) {
      const placeholder = document.createComment('wm-links-placeholder');
      this._wmLinksPlaceholder = placeholder;
      const parent = links.parentNode;
      if (parent) parent.insertBefore(placeholder, links);
    }

    if (links.parentNode !== modelsAside) {
      links.classList.add('wm-links--docked');
      modelsAside.appendChild(links);
    }
  }

  _updateModelPanelsSlider(panel, modelKey) {
    if (!panel) return;
    const sliderEl = panel.querySelector('.navigation-swiper');
    const swiper = sliderEl?.swiper;
    if (!swiper) return;

    swiper.update();
    swiper.updateSlidesClasses();

    const slides = Array.from(panel.querySelectorAll('.wm-model-panel[data-model-panel]'));
    if (!slides.length) return;
    let targetIndex = slides.findIndex(slide => !slide.hidden);
    if (modelKey) {
      const idx = slides.findIndex(slide => slide.getAttribute('data-model-panel') === modelKey);
      if (idx !== -1) targetIndex = idx;
    }

    if (targetIndex < 0) targetIndex = 0;
    swiper.slideTo(targetIndex, 0);

    const autoplayAttr = sliderEl?.dataset.autoplay;
    const autoplayRequested = autoplayAttr !== undefined && autoplayAttr !== 'false';
    if (autoplayRequested && swiper.autoplay && swiper.params.autoplay) {
      requestAnimationFrame(() => {
        if (typeof swiper.autoplay.start === 'function') {
          swiper.autoplay.start();
        }
      });
    }
  }

  // =========================
  // Utilities
  // =========================
  getMenuLabel(key) {
    const btn = document.getElementById(`btn-${key}`);
    const label = btn ? btn.textContent.trim() : '';
    return label || 'Menü';
  }

  // Match Tailwind `xl: 1280px`
  isDesktop() { return window.matchMedia('(min-width: 1280px)').matches; }

  setupGlobalKeys() {
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        if (this.isMobileMenuOpen) this.closeMobileMenu();
        else if (this.activeBtn) this.closeAllSubmenus();
      }
    });
  }

  setupResponsiveBehavior() {
    const onResize = () => {
      const nowDesktop = this.isDesktop();

      // Only act when crossing the 1280px breakpoint
      if (nowDesktop !== this._isDesktop) {
        // Normalize all submenu state
        this.closeAllSubmenus();
        if (this.submenuPanel) this.submenuPanel.classList.remove('active');

        if (nowDesktop) {
          // Entered desktop: ensure any teleported model asides & links are restored first
          this._restoreAllModelAsides();
          this._restoreLinks(this.getWohnmobileSection());
          this._restoreAnyMobileSection();
          // Entered desktop: ensure mobile dialog is fully closed
          if (this.isMobileMenuOpen) this.closeMobileMenu();
          // Safety: clear mobile panes/breadcrumb remnants when returning to desktop
          const listPane   = document.getElementById('wmMobileListPane');
          const detailPane = document.getElementById('wmMobileDetailPane');
          if (listPane) listPane.innerHTML = '';
          if (detailPane) detailPane.innerHTML = '';
          const crumb = this.mobileSubmenu ? this.mobileSubmenu.querySelector('.mega-nav__breadcrumb') : null;
          if (crumb) crumb.remove();
          this._ensureDesktopCloseButtons();
        } else {
          // Entered mobile: desktop hovers are off; nothing else required
          this._removeDesktopCloseButtons();
        }

        // Remember current breakpoint
        this._isDesktop = nowDesktop;
      }
    };

    window.addEventListener('resize', onResize);
    // Run once to normalize on initial load / when toggling device mode
    onResize();
  }

  // Fokusfalle
  installFocusTrap() {
    this._focusTrapHandler = (e) => {
      if (!this.isMobileMenuOpen) return;
      if (!this.mobileMenuOverlay || this.mobileMenuOverlay.hidden) return;
      if (e.key !== 'Tab') return;

      const focusables = Array.from(this.mobileMenuOverlay.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')).filter(el => !el.hasAttribute('disabled'));
      if (focusables.length === 0) {
        // Prevent focus from escaping to <body>; keep it on the overlay container
        e.preventDefault();
        if (this.mobileMenuOverlay) this.mobileMenuOverlay.focus();
        return;
      }
      const first = focusables[0];
      const last  = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', this._focusTrapHandler);
  }
  uninstallFocusTrap() { if (this._focusTrapHandler) document.removeEventListener('keydown', this._focusTrapHandler); }
}

// --- Explicit initializer (recommended for module control) ---
export function initMenu() {
  // Avoid duplicate instances (e.g., during HMR or multiple imports)
  if (window && window.__menuSystem) return window.__menuSystem;
  const instance = new MenuSystem();
  if (window) window.__menuSystem = instance;
  return instance;
}
